/**
 * Stores — what the colony holds, and what it is standing ready to trade.
 *
 * The inventory and the market used to be separate panels on separate screens,
 * and answering "I have too much sulfur, sell it" meant reading a number on one
 * screen and finding the same row again on another. They are one thing: the
 * order controls live on the row for the commodity they apply to.
 *
 * THREE PANELS, NOT ONE. Twenty-nine commodities at the touch size is roughly
 * 1,900 pixels of rows, which as a single panel is a column you scroll forever.
 * Split by kind, they flow into as many columns as the screen has room for and
 * the whole store is visible at once.
 *
 * Minerals carry no trade controls. Nothing buys or sells raw ore — the market
 * deals in refined elements and manufactured goods (see TRADEABLE_IDS), and a
 * control that could not do anything would be worse than its absence.
 */

import {
  ELEMENT_IDS,
  ELEMENT_NAMES,
  GOODS,
  GOOD_IDS,
  INERT_ELEMENT_IDS,
  MINERALS,
  MINERAL_IDS,
  type ElementId,
  type GoodId,
  type MineralId,
} from '../../sim/data';
import { buyPrice, sellPrice } from '../../sim/market';
import type { GameState, MarketOrder, TradeableId } from '../../sim/types';
import { compact, el, section, setText, type Issue, type Panel } from './panel';

const MODES: Array<MarketOrder['mode']> = ['idle', 'sell', 'buy'];

const MODE_LABEL: Record<MarketOrder['mode'], string> = {
  idle: '—',
  sell: 'sell',
  buy: 'buy',
};

interface TradeRow {
  root: HTMLElement;
  held: HTMLElement;
  price: HTMLElement;
  mode: HTMLButtonElement;
  threshold: HTMLInputElement;
  /** Last value seen from state, so the handler can send the NEXT one. */
  known: MarketOrder;
}

/**
 * One commodity: what is held, what it is worth, and the standing order.
 *
 * The mode cycles rather than offering three buttons — there are twenty-one
 * tradeable rows and three buttons apiece would be sixty-three targets. The
 * threshold means different things per mode, which the placeholder says: a sell
 * order keeps that many in reserve, a buy order tops up to that many. One
 * number, two readings, but the same question from either side — how many do I
 * want to end up holding?
 */
function tradeRow(issue: Issue, id: TradeableId, label: string): TradeRow {
  const root = el('div', 'store-row');

  const name = el('span', 'row-label', label);
  const held = el('span', 'store-held', '0');
  const price = el('span', 'store-price', '—');

  const mode = el('button', 'prio store-mode', MODE_LABEL.idle);
  const threshold = el('input', 'threshold') as HTMLInputElement;
  threshold.type = 'number';
  threshold.min = '0';
  threshold.value = '0';

  const row: TradeRow = {
    root,
    held,
    price,
    mode,
    threshold,
    known: { mode: 'idle', threshold: 0 },
  };

  mode.addEventListener('click', () => {
    const next = MODES[(MODES.indexOf(row.known.mode) + 1) % MODES.length] as MarketOrder['mode'];
    issue({ type: 'setMarketOrder', element: id, mode: next, threshold: row.known.threshold });
  });

  threshold.addEventListener('change', () => {
    const parsed = Number.parseInt(threshold.value, 10);
    const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    issue({ type: 'setMarketOrder', element: id, mode: row.known.mode, threshold: value });
  });

  root.append(name, held, price, mode, threshold);
  return row;
}

function refresh(row: TradeRow, state: GameState, id: TradeableId, amount: number): void {
  const order = state.market.orders[id];
  row.known = order;

  setText(row.held, compact(amount));
  row.root.classList.toggle('dim', amount === 0 && order.mode === 'idle');

  // The price shown is the one the standing order would transact at: what you
  // would receive when selling, what you would pay when buying. Showing a
  // single mid price would misstate both by the spread.
  const shown = order.mode === 'buy' ? buyPrice(state.market, id) : sellPrice(state.market, id);
  setText(row.price, compact(shown));

  setText(row.mode, MODE_LABEL[order.mode]);
  row.mode.classList.toggle('toggle-on', order.mode !== 'idle');
  row.threshold.placeholder = order.mode === 'buy' ? 'up to' : 'keep';

  // Never overwrite a box being typed into: this runs every frame.
  if (document.activeElement !== row.threshold) {
    row.threshold.value = order.threshold === 0 ? '' : String(order.threshold);
  }
}

/** Raw ore. Held only — the market does not deal in it. */
export function createMineralStorePanel(): Panel {
  const { root, body } = section('Minerals');

  const rows = {} as Record<MineralId, { root: HTMLElement; value: HTMLElement }>;
  for (const id of MINERAL_IDS) {
    const row = el('div', 'row');
    const swatch = el('span', 'row-swatch');
    swatch.style.background = `var(--mineral-${id})`;
    const value = el('span', 'row-value', '0');
    row.append(swatch, el('span', 'row-label', MINERALS[id].label), value);
    body.append(row);
    rows[id] = { root: row, value };
  }

  return {
    root,
    update(state: GameState): void {
      for (const id of MINERAL_IDS) {
        const amount = state.inventory.minerals[id];
        setText(rows[id].value, compact(amount));
        rows[id].root.classList.toggle('dim', amount === 0);
      }
    },
  };
}

export function createElementStorePanel(issue: Issue): Panel {
  const { root, body } = section('Elements');
  body.append(
    el('div', 'hint', 'Sell keeps a reserve; buy tops up to a target.'),
    el('div', 'store-head-row store-row'),
  );

  const head = body.lastElementChild as HTMLElement;
  head.append(
    el('span', 'row-label', ''),
    el('span', 'store-held', 'held'),
    el('span', 'store-price', 'each'),
    el('span', 'store-mode-head', 'order'),
    el('span', ''),
  );

  // Elements in INERT_ELEMENT_IDS are byproducts no recipe consumes, so they are
  // labelled — otherwise a counter that only ever climbs reads as a bug rather
  // than as the expected behaviour it is. They are still worth selling.
  const inert = new Set<ElementId>(INERT_ELEMENT_IDS);
  const rows = {} as Record<ElementId, TradeRow>;
  for (const id of ELEMENT_IDS) {
    const name = ELEMENT_NAMES[id];
    const row = tradeRow(issue, id, inert.has(id) ? `${name} (waste)` : name);
    rows[id] = row;
    body.append(row.root);
  }

  return {
    root,
    update(state: GameState): void {
      for (const id of ELEMENT_IDS) {
        refresh(rows[id], state, id, state.inventory.elements[id]);
      }
    },
  };
}

export function createGoodStorePanel(issue: Issue): Panel {
  const { root, body } = section('Components');

  const rows = {} as Record<GoodId, TradeRow>;
  for (const id of GOOD_IDS) {
    const row = tradeRow(issue, id, GOODS[id].label);
    rows[id] = row;
    body.append(row.root);
  }

  return {
    root,
    update(state: GameState): void {
      for (const id of GOOD_IDS) {
        refresh(rows[id], state, id, state.inventory.goods[id]);
      }
    },
  };
}
